/**
 * App.react.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { createStructuredSelector } from 'reselect';
import autobind from 'class-autobind';

import classNames from 'classnames';

// Components
import Authentication from 'pages/Authentication';
import BreadcrumbBar from 'components/BreadcrumbBar';
import Button from 'components/Button';
import ClickWrapper from 'components/ClickWrapper';
import Icon from 'components/Icon';
import IconicList from 'components/IconicList';
import Markdown from 'components/Markdown';
import Modal from 'components/Modal';
import PageHeader from 'components/PageHeader';
import Search from 'pages/Search';
import SiteAlert from 'components/SiteAlert';
import SiteFooter from 'components/SiteFooter';
import SiteHeader from 'components/SiteHeader';
import SiteMenu from 'components/SiteMenu';
import TertiaryMenu from 'components/TertiaryMenu';

// Selectors
import { selectProductLine } from 'store/fonts/selectors';

// Import configs
import { app as AppLang, common as CommonLanguage, socialMeta as SocialMeta } from 'language';
import { cdn, keyCodes } from 'config';

// Utilities
import {
	disableBodyScrolling,
	enableBodyScrolling,
	getFocusedLicense,
	loadWOFFs,
	hashSHA256
} from 'utils';

import {
	dataLayer
} from 'utils/analytics';

import { isAuthenticated as authenticated } from 'utils/authentication';

// Content
import lists from 'content/lists';

// Styles
import styles from './styles.css';
import formStyles from 'styles/forms.css';

// Lodash
import {
	extend,
	findKey,
	forEach,
	get,
	isEmpty,
	isEqual,
	last,
	lowerCase,
	startsWith,
	merge
} from 'lodash';

// We import this here so that the global Lost grid settings kick in App-wide.
// For some reason including them in CSS alone wasn't triggering them.
require('styles/grid.css');

import breakpointMatches from 'utils/breakpoints';

const ERROR_MESSAGE = 'It looks like something went wrong. Please try again, and if you’re still having trouble, [let us know](/contact) and we’ll see how we can help.';

// Actions
import {
	addToCart,
	clearError,
	closeAuthenticationModal as closeAuthenticationModalAction,
	getCart,
	isMenuOpen as menuState,
	preloadSubMenus,
	setActiveBreakpoints,
	setBreadcrumbState,
	setIsAuthenticated,
	setMenu,
	setMobileState,
	setOverlayVisibility,
	setSubMenu,
	setTertiaryMenu,
	subscribeNewsletter as subscribeNewsletterAction
} from './actions';
import { updateLicense } from 'store/licenses/actions';

// Selectors
import {
	selectAdditional,
	selectAlert,
	selectAuthenticationModal,
	selectAuthState,
	selectBreadcrumbState,
	selectBreakpoints,
	selectCart,
	selectCartLoading,
	selectCollections,
	selectCurrentBlogTitle,
	selectError,
	selectFooter,
	selectIsMenuOpen,
	selectMenu,
	selectMenuContents,
	selectOverlayState,
	selectPageHeader,
	selectPreloadedSubMenusLoaded,
	selectSiteHeaderState,
	selectSubMenu,
	selectSubMenuContents,
	selectSubMenuSnapshot,
	selectTertiaryMenu
} from './selectors';
import { selectUserProfile } from 'store/account/selectors';
import { selectLicenseState } from 'store/licenses/selectors';

const COLUMN_COUNT = 13;
const PAGEVIEW_TIMER = 800;

import { localStore } from 'utils/storageFactory';

export class App extends React.Component {
	isMounted = false;

	static childContextTypes = {
		setPageTitleFromRoute: PropTypes.func
	};

	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			additionalMenuItems: null,
			pageTitle: null,
			previousPageTitle: null,
			tertiaryMenuIsSet: false,
			toolsBreakpointsVisible: 'n',
			toolsShowGrid: 'n',
			isShortNameInBreadcrumb: false, // for mobile xSmall layout only, allows for short name at smallest sizes
			isMobileSiteMenu: false, // for mobile layout only, allows for mobile exclusive hiding and showing of fonts submenus
			shouldSendPageView: false,
			showMobileFontsMenu: false // for mobile layout only, when user clicks on "browse by style" or "find by name", the main menu hides and submenu shows
		};

		this.PageviewTimer = null;
	}

	getChildContext() {
		return {
			setPageTitleFromRoute: this.setPageTitleFromRoute
		};
	}

	componentDidMount() {
		this.isMounted = true;

		const {
			menu,
			onGetCart,
			onPreloadSubMenus,
			onSetActiveBreakpoints,
			onUpdateMobile,
			user
		} = this.props;

		// Set the page title
		this.setPageTitleFromRoute(this.props.routes);

		// Log the user’s ID
		if (
			!isEmpty(user)
			&& authenticated()
		) {
			hashSHA256(lowerCase(user.email)).then((cjEmailHash) => {
				dataLayer({
					cjEmailHash,
					userId: Number(user.id),
					userEmail: user.email,
					userName: user.name,
					userPhone: user.phone
				});
			}).catch(() => {
				dataLayer({
					userId: Number(user.id),
					userEmail: user.email,
					userName: user.name,
					userPhone: user.phone
				});
			});
		}

		// Log a pageview
		if (!authenticated()) {
			this.sendPageview();
		} else {
			this.setState({ shouldSendPageView: true });
		}

		// Collection of webfonts to load up front. These styles either appear
		// throughout the site, or are in prominent positions.
		const woffs = [
			// Ideal Sans
			'Ideal Sans Semibold',
			'Ideal Sans Extra Light',
			'Ideal Sans SSm Extra Light',
			'Ideal Sans SSm Extra Light Italic',
			'Ideal Sans SSm Book Italic',
			'Ideal Sans SSm Book',
			'Ideal Sans SSm Medium',
			'Ideal Sans SSm Medium Italic',
			'Ideal Sans SSm Semibold Italic',
			'Ideal Sans SSm Semibold',
			'Ideal Sans SSm Bold',
			'Ideal Sans SSm Bold Italic',
			// Idlewild
			'Idlewild Light',
			'Idlewild SSm Bold',
			// Operator
			'Operator SSm Light',
			// Sentinel
			'Sentinel Black',
			'Sentinel SSm Black',
			'Sentinel SSm Bold',
			'Sentinel SSm Bold Italic',
			'Sentinel Bold',
			'Sentinel Bold Italic',
			'Sentinel Semibold',
			'Sentinel SSm Semibold',
			'Sentinel SSm Book',
			'Sentinel SSm Book Italic',
			'Sentinel Light Italic',
			'Sentinel Light'
		];
		if (this.isMounted) {
			loadWOFFs(woffs);
		}

		// Preload Fonts menu’s default submenu
		onPreloadSubMenus();

		// Set isMobile state
		if (onUpdateMobile) { onUpdateMobile(global.hasTouchEvents); }

		/**
		 * Setting up media query listeners based on select settings we have
		 * stored in the breakpoints config
		 */
		let currentBreakpoints = {};
		forEach(breakpointMatches, (breakpoint) => {
			currentBreakpoints = { ...this.getBreakpointMatch(breakpoint), ...currentBreakpoints };
			breakpoint.addListener(this.onBreakpointChange);
		});

		// Update the store with breakpoint values at mount time
		if (onSetActiveBreakpoints) { onSetActiveBreakpoints(currentBreakpoints); }

		// Set auth state
		if (this.props.setIsAuthenticated) {
			this.props.setIsAuthenticated(authenticated());
		}

		if (process.env.NODE_ENV !== 'test') {
			this.setState({
				toolsBreakpointsVisible: JSON.parse(localStore.getItem('toolsBreakpointsVisible')),
				toolsShowGrid: JSON.parse(localStore.getItem('toolsShowGrid'))
			});
		}

		if (menu && menu !== '') {
			disableBodyScrolling();
		} else {
			enableBodyScrolling();
		}

		const route = last(this.props.routes);
		if (route.name !== 'cart') {
			onGetCart();
		}

		document.addEventListener('keydown', this.keyboardEvents, false);
	}


	componentDidUpdate(prevProps) {
		const {
			isAuthenticated,
			location,
			onGetCart,
			onSetTertiaryMenu,
			route,
			user
		} = this.props;

		// Log the user’s ID
		if (
			isEmpty(prevProps.user)
			&& !isEmpty(user)
			&& isAuthenticated
		) {
			hashSHA256(lowerCase(user.email)).then((cjEmailHash) => {
				const userAnalyticsData = {
					cjEmailHash,
					userId: Number(user.id),
					userEmail: user.email,
					userName: user.name,
					userPhone: user.phone
				};
				dataLayer(userAnalyticsData);
				if (this.state.shouldSendPageView) {
					this.sendPageview(userAnalyticsData);
					this.setState({ shouldSendPageView: false });
				}
			}).catch(() => {
				const userAnalyticsData = {
					userId: Number(user.id),
					userEmail: user.email,
					userName: user.name,
					userPhone: user.phone
				};
				dataLayer(userAnalyticsData);
				if (this.state.shouldSendPageView) {
					this.sendPageview(userAnalyticsData);
					this.setState({ shouldSendPageView: false });
				}
			});
		}

		// Unset the user’s ID
		if (
			!isEmpty(prevProps.user)
			&& isEmpty(user)
			// && this.props.isAuthenticated
		) {
			dataLayer({
				cjEmailHash: undefined,
				userId: undefined
			});
		}


		// Log a pageview
		if (prevProps.location.pathname !== location.pathname) {
			this.sendPageview();
		}

		if (prevProps.location !== location) {
			// Close any open menus when changing routes
			onSetTertiaryMenu(null);
		}

		if (route.name !== 'cart' && prevProps.isAuthenticated !== isAuthenticated) {
			onGetCart();
		}

		this.setMenusFromParams(prevProps);
	}

	componentWillUnmount() {
		this.isMounted = false;

		// Remove the media query listener
		forEach(breakpointMatches, (breakpoint) => {
			breakpoint.removeListener(this.onBreakpointChange);
		});

		document.removeEventListener('keydown', this.keyboardEvents);
	}


	/**
	 * Updates component state based on te media queries that were passed in
	 * during componentWillMount().
	 * @method onBreakpointChange
	 * @param  {Object} query
	 */
	onBreakpointChange(query) {
		const print = window.matchMedia('print');
		if (!print.matches) {
			this.props.onSetActiveBreakpoints(this.getBreakpointMatch(query));
			const path = this.props.location.pathname;
			if (path.includes('/account/order-history/')) {
				var x = document.getElementById('root');
				var y = document.getElementsByTagName('main');
				if (x.style.visibility) {
					x.style.removeProperty('visibility');
					y[0].style.removeProperty('visibility');
					y[0].style.marginTop = '0px';
					y[0].style.webkitMarginBefore = '0px';
				}
			}
		} else {
			const path = this.props.location.pathname;
			if (path.includes('/account/order-history/')) {
				var x = document.getElementById('root');
				var y = document.getElementsByTagName('main');
				x.style.visibility = 'hidden';
				y[0].style.visibility = 'visible';
				y[0].style.marginTop = '-250px';
				y[0].style.webkitMarginBefore = '-250px';
			}
		}
	}

	setMenusFromParams(prevProps) {
		let { subMenu } = this.props.location.query;
		const { menu } = this.props.location.query;

		if (menu) {
			if (
				(this.props.preloadedSubMenusLoaded && !prevProps.preloadedSubMenusLoaded ||
				!isEqual(this.props.location.query, prevProps.location.query)) &&
				this.props.secondaryMenu !== subMenu
			) {
				subMenu = subMenu || menu;
				if (this.props.breakpoints.ltLargeMedium) {
					this.setState({ isMobileSiteMenu: true, showMobileFontsMenu: menu === 'fonts' && !!subMenu });
				}
				this.props.onSetSubMenu({ menu: subMenu });
			}
			if (
				(((this.props.subMenuSnapshot && !prevProps.subMenuSnapshot) && (this.props.subMenuSnapshot === (subMenu || menu))) ||
				(!subMenu && (this.props.secondaryMenu !== prevProps.secondaryMenu) && this.props.secondaryMenu === menu) ||
				!isEqual(this.props.location.query, prevProps.location.query)) &&
				this.props.menu !== menu
			) {
				this.props.onSetMenu(menu);
			}
		}
	}

	getBreakpointMatch(query) {
		const breakpoint = findKey(breakpointMatches, (bp) => bp.media === query.media);
		return { [breakpoint]: query.matches };
	}


	/**
	 * Get page <title /> from route data
	 * @method  setPageTitleFromRoute
	 * @param   {Object}  [routes=null]  Route data to scour to `pageTitle`.
	 *								   Can be `this.props.routes` or even
	 *								   `nextProps.routes`
	 */
	setPageTitleFromRoute(routes = null) {
		const currentRoute = last(routes);
		const pageTitle = get(currentRoute, 'pageTitle', undefined);
		this.setState({ pageTitle });
	}


	keyboardEvents(event) {
		// ESC
		if (event.keyCode === keyCodes.ESCAPE) {
			if (this.props.selectedTertiaryMenu !== null) { this.props.onSetTertiaryMenu(null); }

			// close menu overlay if open
			if (!isEmpty(this.props.menu)) {
				this.closeMenus();
			}
		}
	}


	/**
	 * Handles updating newly- and previously-selected licenses by updating
	 * the store accordingly. Currently only manages Apps and Websites.
	 *
	 * @param  {String} type License type that needs updating.
	 */
	focusLicenseType(type) {
		const { licenses, onUpdateLicense } = this.props;

		// Update the value for the selected license
		const updatedLicenses = {};

		// Ensure that any previously focused license gets unlocked
		const prevLicense = findKey(licenses, (theLicense) => theLicense.focused === true);
		if (!isEmpty(prevLicense)) {
			updatedLicenses[prevLicense] = { focused: false };
		}

		// Set the selected license
		if (prevLicense !== type) {
			updatedLicenses[type] = { focused: true };
		}

		if (!isEmpty(updatedLicenses)) {
			onUpdateLicense(updatedLicenses);
		}

		// Enable the overlay if the selected license is "cloud" or "app"
		// and the value hasn’t yet been set. This means the visitor needs
		// to select the subscription level.
		this.props.onSetOverlayVisibility(
			(type === 'cloud' || type === 'app') &&
			updatedLicenses[type].focused
		);
	}


	closeOverlay() {
		this.props.onSetOverlayVisibility(false);
		const selectedLicense = getFocusedLicense(this.props.licenses);
		if (selectedLicense) { this.focusLicenseType(selectedLicense); }
	}


	/**
	 * Okay, this one’s stupid fragile, but after some research it seems like
	 * this is actually tricky to get right with SPAs. The primary issue with
	 * our app is that after a route change the page title can take several
	 * hundred milliseconds to update. This is partly due to react-helmet and
	 * the depth level with the DOM elements we’re working with on various pages.
	 * They’re determined to improve with their next release, but until then we
	 * need to use a timer.
	 *
	 * @method sendPageview
	 * @return {Void}
	 */
	sendPageview(userAnalyticsData) {
		clearTimeout(this.PageviewTimer);

		this.PageviewTimer = setTimeout(() => {
			const previousPageTitle = document.title;
			if (previousPageTitle === this.state.previousPageTitle) { return; }

			const { cart, routes } = this.props;
			const route = last(routes);
			const { cjPageType } = route;

			// Send pageview event to Google Tag Manager
			dataLayer({
				...userAnalyticsData,
				event: 'Pageview',
				cjPageType,
				cjOrderSubtotal: cjPageType === 'cart' ? get(cart, 'subtotal') : null
			});

			this.setState({ previousPageTitle });
		}, PAGEVIEW_TIMER);
	}


	closeMenus() {
		const { changeRoute, location, onSetMenu, onSetSubMenu } = this.props;

		if (this.state.isMobileSiteMenu) {
			this.setState({ showMobileFontsMenu: false });
		}

		const query = merge({}, location.query);
		delete query.menu;
		delete query.subMenu;
		changeRoute(extend({}, location, { query }));
		onSetMenu('');
		onSetSubMenu('');
		enableBodyScrolling();
	}


	handleBreakpointToggle() {
		const showBreakpointTool = this.state.toolsBreakpointsVisible === 'y' ? 'n' : 'y';
		this.setState({
			toolsBreakpointsVisible: showBreakpointTool
		});

		if (process.env.NODE_ENV !== 'test') {
			localStore.setItem('toolsBreakpointsVisible', JSON.stringify(showBreakpointTool));
		}
	}


	handleGridToggle() {
		const isShown = this.state.toolsShowGrid === 'y' ? 'n' : 'y';
		this.setState({
			toolsShowGrid: isShown
		});

		if (process.env.NODE_ENV !== 'test') {
			localStore.setItem('toolsShowGrid', JSON.stringify(isShown));
		}
	}


	UNSAFE_componentWillReceiveProps(nextProps) { // eslint-disable-line camelcase
		const nextRoute = last(nextProps.routes);
		const { breakpoints } = nextProps;

		// should we update the page title?
		if (this.props.location !== nextProps.location) {
			this.setPageTitleFromRoute(nextProps.routes);
		}

		// If `selectedTertiaryMenu` changes, update state
		if (!isEqual(this.props.selectedTertiaryMenu, nextProps.selectedTertiaryMenu)) {
			this.setState({ tertiaryMenuIsSet: !!nextProps.selectedTertiaryMenu });

			if (nextProps.selectedTertiaryMenu) {
				disableBodyScrolling();
			} else {
				enableBodyScrolling();
			}
		}

		/**
		 * Check the next route for a `breadcrumbBar` attribute. If it exists
		 * and is true, use the BreadcrumBar for navigation. If it’s false,
		 * reset the breadcrumb to hide it. Relies on location.pathname value
		 * changing, and the current breakpoint to be `largeMedium` or below.
		 */
		const currentRoute = last(nextRoute);
		const withBreadcrumb = get(currentRoute, 'breadcrumbBar', false);
		if (
			!withBreadcrumb
			&& !isEqual(this.props.location.pathname, nextProps.location.pathname)
			&& this.props.breakpoints.ltLargeMedium
		) {
			this.props.setBreadcrumb({
				parent: null,
				current: {
					label: CommonLanguage.companyNameShort,
					path: '/',
					tagLine: null
				},
				showSecondary: false,
				// TODO: Eliminate need for below
				// A refactor of how the BreadcrumbBar / TertiaryMenu are configured
				// is likely needed - it's getting complex
				tertiaryMenu: nextRoute.tertiaryMenu || null,
				tertiaryMenuLabel: nextRoute.tertiaryMenu && lists[nextRoute.tertiaryMenu].menuLabel
			}, nextProps.menu ? 'menu' : 'page');
		}

		if (!isEqual(this.props.secondaryMenuContents, nextProps.secondaryMenuContents)) {
			if (nextProps.menu === 'fonts' && nextProps.secondaryMenuContents) {
				this.props.setBreadcrumb({
					onclick: () => {
						if (this.state.isMobileSiteMenu) {
							this.setState({ showMobileFontsMenu: false });
						} else {
							this.props.onSetSubMenu('');
						}
					},
					parent: { label: 'Fonts' },
					current: { label: nextProps.secondaryMenuContents.title },
					showSecondary: false,
					tertiaryMenu: nextRoute.tertiaryMenu || null,
					tertiaryMenuLabel: nextRoute.tertiaryMenu && lists[nextRoute.tertiaryMenu].menuLabel
				}, 'menu');
			}
		}

		if (!isEqual(this.props.additionalMenuItems, nextProps.additionalMenuItems)) {
			this.setState({ additionalMenuItems: nextProps.additionalMenuItems });
		}

		if (this.props.menu !== nextProps.menu) {
			if (nextProps.menu && nextProps.menu !== '') {
				disableBodyScrolling();
			} else if (!nextProps.menu || nextProps.menu === '') {
				enableBodyScrolling();
			}
		}

		if (this.props.breakpoints.ltLargeMedium !== breakpoints.ltLargeMedium || this.props.breakpoints.gtLargeMedium !== breakpoints.gtLargeMedium) {
			// Switch between ltLargeMedium & gtLargeMedium if/else-if results
			// in less setState calls than a standard if/else.
			if (breakpoints.ltLargeMedium) {
				this.setState({ isMobileSiteMenu: true });
			} else if (breakpoints.gtLargeMedium) {
				this.setState({ isMobileSiteMenu: false, showMobileFontsMenu: false });
			}
		}

		if (this.props.breakpoints.ltSmall !== breakpoints.ltSmall) {
			if (breakpoints.ltSmall) {
				this.setState({ isShortNameInBreadcrumb: true });
			}
		}

		if (this.props.breakpoints.gtSmall !== breakpoints.gtSmall) {
			if (breakpoints.gtSmall) {
				this.setState({ isShortNameInBreadcrumb: false });
			}
		}

		if (!isEmpty(this.props.breakpoints)) {
			if (this.props.breakpoints.ltLargeMedium !== breakpoints.ltLargeMedium || this.props.breakpoints.gtLargeMedium !== breakpoints.gtLargeMedium) {
				// Closing the menu when switching from mobile layout to desktop
				// layout cleans up A LOT of edge-case scenarios and syncing issues
				this.closeMenus();
			}

			if (this.props.breakpoints.ltSmall !== breakpoints.ltSmall) {
				this.closeMenus();
			}

			if (this.props.breakpoints.gtSmall !== breakpoints.gtSmall) {
				this.closeMenus();
			}
		}
	}

	toggleMobileFontMenuView(toggle) {
		this.setState({ showMobileFontsMenu: toggle });
	}


	renderErrorMessage() {
		const { error } = this.props;

		if (!error || error.type !== 'message') {
			return null;
		}

		return (
			<div className={ styles.errorMessageContainer }>
				<Markdown
					className={ styles.errorText }
					content={ error.message || ERROR_MESSAGE }
					tagName="p"
				/>
			</div>
		);
	}


	renderErrorModal() {
		const { changeRoute, error, onClearError } = this.props;

		if (!error || error.type !== 'modal') {
			return null;
		}

		return (
			<Modal containerClassName={ styles.errorModalContainer } onClose={ onClearError } title={ error.title }>
				<ClickWrapper changeRoute={ changeRoute }>
					<Markdown
						className={ styles.errorText }
						content={ error.message || ERROR_MESSAGE }
						tagName="div"
					/>
				</ClickWrapper>
				<div className={ formStyles.actions }>
					<Button
						focus
						className={ styles.modalButton }
						color="secondary"
						onClick={ onClearError }
					>
						OK
					</Button>
				</div>
			</Modal>
		);
	}


	/**
	 * Render Helmet node with social media card data
	 * @method  renderHelmet
	 * @return  {Node}
	 */
	renderHelmet() {
		const { routes } = this.props;
		const route = last(routes);
		const pageTitle = route.pageTitle;
		const title = pageTitle ? `${pageTitle} | ${AppLang.pageTitle}` : AppLang.pageTitle;
		const image = `${cdn}${SocialMeta.image}`;
		const description = SocialMeta.summary;

		return (
			<Helmet
				defaultTitle={ AppLang.pageTitle }
				titleTemplate={ `%s | ${AppLang.pageTitle}` }
			>
				<title>{ this.state.pageTitle }</title>
				<link href={ `https://typography.com${location.pathname}` } rel="canonical" />

				<meta content={ description } name="description" />
				<meta content="width=device-width, initial-scale=1" name="viewport" />

				{/* Twitter */}
				<meta content={ description } name="twitter:description" />
				<meta content={ image } name="twitter:image" />
				<meta content={ window.location.href } name="twitter:url" />
				<meta content={ title } name="twitter:title" />

				{/* Facebook, etc. */}
				<meta content={ location } property="og:url" />
				<meta content="article" property="og:type" />
				<meta content={ description } property="og:description" />
				<meta content={ image } property="og:image" />
				<meta content={ title } property="og:title" />
			</Helmet>
		);
	}


	/**
	 * Render site menu
	 *
	 * Determines offset to render the menu at (based on breakpoint and page
	 * header) and what the contents of the menu should be.
	 *
	 * @method  renderSiteMenu
	 * @return  {Node}  The SiteMenu
	 */
	renderSiteMenu() {
		const {
			breakpoints,
			changeRoute,
			isMenuOpen,
			location,
			onIsMenuOpen,
			menuContents,
			menu,
			onSetSubMenu,
			onSetMenu,
			secondaryMenu,
			secondaryMenuContents,
			user,
			licenses
		} = this.props;

		const { additionalMenuItems } = this.state;

		let offset;
		if (menu === 'account' && breakpoints.ltLargeMedium) {
			offset = 'aboveBreadcrumbBar';
		}

		return (
			<SiteMenu
				additionalMenuItems={ additionalMenuItems }
				breakpoints={ breakpoints }
				changeRoute={ changeRoute }
				isMenuOpen={ isMenuOpen }
				isMobileSiteMenu={ this.state.isMobileSiteMenu }
				isShortNameInBreadcrumb={ this.state.isShortNameInBreadcrumb }
				licenses={ licenses }
				location={ location }
				menu={ menuContents }
				offset={ offset }
				onIsMenuOpen={ onIsMenuOpen }
				onSetMenu={ onSetMenu }
				onSetSubMenu={ onSetSubMenu }
				secondaryMenu={ secondaryMenu }
				secondaryMenuContents={ secondaryMenuContents }
				showMobileFontsMenu={ this.state.showMobileFontsMenu }
				toggleMobileFontMenuView={ this.toggleMobileFontMenuView }
				user={ user }
			/>
		);
	}


	render() {
		const {
			alert,
			authenticationModal,
			breadcrumb,
			breakpoints,
			cart,
			changeRoute,
			closeAuthenticationModal,
			children,
			footer,
			isAuthenticated,
			location,
			menu,
			onSetSubMenu,
			onSetMenu,
			onSetTertiaryMenu,
			overlayIsVisible,
			pageHeader,
			params,
			productLine,
			route,
			routes,
			secondaryMenu,
			selectedTertiaryMenu,
			siteHeader,
			subscribeNewsletter,
			user
		} = this.props;

		const { tertiaryMenuIsSet, toolsBreakpointsVisible, toolsShowGrid } = this.state;

		// Get current route from path
		const currentRoute = last(this.props.routes);

		// Get tertiary menu label if there is one
		let tertiaryMenuLabel;
		if (currentRoute.tertiaryMenu) {
			tertiaryMenuLabel = lists[currentRoute.tertiaryMenu].menuLabel;
		}

		// PageHeader settings
		const hasPageHeaderCollapsed = get(pageHeader, 'size') === 'small' || get(pageHeader, 'size') === 'simple';

		const errorMessage = this.renderErrorMessage();

		let currentBreadcrumb = breadcrumb.page;
		if (menu && !['account', 'search'].includes(menu)) {
			currentBreadcrumb = breadcrumb.menu;
		}

		// Inject fontID into 'selectedTertiaryMenu' IconicList to create the
		// external 'try' link in the details dropdown menu for mobile view.
		const fontID = (productLine && productLine.id) ? productLine.id : null;

		const path = this.props.location.pathname;
		const currentPage = path.substring(path.lastIndexOf('/') + 1);

		const parameters = { ...params };
		if (currentRoute.tertiaryMenu === 'font') {
			parameters.fontID = fontID;
		}

		return (
			<div
				className={ classNames(
					hasPageHeaderCollapsed ? styles['wrapper--withPageHeader'] : styles.wrapper,
					[styles[`wrapper--${currentPage}`]]
				) }
			>
				{ this.renderHelmet() }

				{ /* Global site header */ }
				<div className={ styles.headerRegion }>
					<SiteHeader
						cart={ cart }
						changeRoute={ changeRoute }
						currentRoute={ routes[1] }
						favoritesCount={ get(user, 'favoritesCount') }
						isAuthenticated={ isAuthenticated }
						isDimmed={ get(siteHeader, 'isDimmed') }
						location={ location }
						menu={ menu }
						onSetMenu={ onSetMenu }
						onSetSubMenu={ onSetSubMenu }
						routes={ route.childRoutes }
						selectedMenu={ menu }
						selectedSubMenu={ secondaryMenu }
						breakpoints={ breakpoints }
					/>
					{ menu && menu !== 'search' && this.renderSiteMenu() }
					{ menu && menu === 'search' && <Search breakpoints={ breakpoints } layout="menu" theme="dark" /> }
				</div>

				{ breakpoints.gtLargeMedium && currentRoute.pageHeader && !pageHeader.useLocal &&
					<PageHeader
						isReady
						breakpoints={ breakpoints }
						location={ location }
						{ ...currentRoute.pageHeader }
					/>
				}

				{ /* Global breadcrumb bar (displayed only below largeMedium breakpoint) */ }
				{ breakpoints.ltLargeMedium &&
					<div className={ styles.breadcrumbRegion }>
						<BreadcrumbBar
							{ ...this.props }
							breakpoints={ breakpoints }
							changeRoute={ changeRoute }
							isMobileSiteMenu={ this.state.isMobileSiteMenu }
							isShortNameInBreadcrumb={ this.state.isShortNameInBreadcrumb }
							onSetTertiaryMenu={ (theMenu) => onSetTertiaryMenu(theMenu) }
							routes={ route.childRoutes }
							selectedMenu={ menu }
							setMenu={ onSetMenu }
							setSubMenu={ onSetSubMenu }
							showMobileFontsMenu={ this.state.showMobileFontsMenu }
							tertiaryMenu={ currentRoute.tertiaryMenu }
							tertiaryMenuIsSet={ tertiaryMenuIsSet }
							tertiaryMenuLabel={ tertiaryMenuLabel }
							{ ...currentBreadcrumb }
						/>
					</div>
				}

				{ overlayIsVisible &&
					<div
						className={ styles.overlay }
						onClick={ () => this.closeOverlay() }
						role="button"
						tabIndex="0"
					/>
				}

				{ selectedTertiaryMenu &&
					<TertiaryMenu>
						<IconicList
							changeRoute={ (theRoute) => {
								let newRoute;
								if (startsWith(theRoute, './')) {
									const newPage = theRoute.replace(/.\/{1}/g, '');
									newRoute = path.replace(currentPage, newPage);
								} else { newRoute = theRoute; }
								changeRoute(newRoute);
							} }
							fontID={ fontID }
							format="tertiaryMenu"
							list={ selectedTertiaryMenu }
							productLine={ productLine }
							segments={ { parameters } }
						/>
					</TertiaryMenu>
				}

				{ authenticationModal &&
					<Authentication
						closeModal={ closeAuthenticationModal }
						signInType="modal"
						{ ...authenticationModal }
					/>
				}

				<main className={ styles.contentRegion } role="main">
					{ errorMessage || children }
				</main>

				{ !isAuthenticated && !isEmpty(footer) &&
					<SiteFooter
						breakpoints={ breakpoints }
						className={ styles.footerRegion }
						onSubmit={ subscribeNewsletter }
						{ ...footer }
					/>
				}
				<SiteAlert { ...alert } />

				{ this.renderErrorModal() }

				{/* Development-only utilities. */}
				{
					process.env.NODE_ENV === 'development' &&
					<div>
						<div className={ classNames(styles.breakpointReporter, { [styles['breakpointReporter--visible']]: toolsBreakpointsVisible === 'y' }) } />
						<div className={ [styles.grid, toolsShowGrid === 'y' ? styles['grid--isVisible'] : null].join(' ') }>
							{ Array(COLUMN_COUNT).fill().map((_, i) => <div className={ styles.column } key={ `gridLine-${i}` } />) }
						</div>
						<div className={ [styles.tools, toolsBreakpointsVisible === 'y' ? styles['tools--breakpointsVisible'] : null].join(' ') }>
							<button
								className={ [styles.tool, toolsShowGrid === 'y' ? styles['tool--isActive'] : null].join(' ') }
								onClick={ this.handleGridToggle }
							>
								<Icon iconName="grid" />
							</button>
							<button
								className={ [styles.tool, toolsBreakpointsVisible === 'y' ? styles['tool--isActive'] : null].join(' ') }
								onClick={ this.handleBreakpointToggle }
							>
								<Icon iconName="breakpoints" />
							</button>
						</div>
					</div>
				}
			</div>
		);
	}
}

App.propTypes = {
	additionalMenuItems: PropTypes.array,
	alert: PropTypes.object,
	authenticationModal: PropTypes.object,
	breadcrumb: PropTypes.object,
	breakpoints: PropTypes.object.isRequired,
	cart: PropTypes.object,
	cartLoading: PropTypes.bool,
	changeRoute: PropTypes.func.isRequired,
	children: PropTypes.node,
	closeAuthenticationModal: PropTypes.func.isRequired,
	error: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.shape({ type: PropTypes.string, message: PropTypes.string })
	]),
	footer: PropTypes.object,
	isAuthenticated: PropTypes.bool,
	isMenuOpen: PropTypes.bool,
	licenses: PropTypes.object,
	location: PropTypes.object,
	menu: PropTypes.string,
	menuContents: PropTypes.object,
	onAddToCart: PropTypes.func.isRequired,
	onClearError: PropTypes.func.isRequired,
	onGetCart: PropTypes.func.isRequired,
	onIsMenuOpen: PropTypes.func,
	onPreloadSubMenus: PropTypes.func.isRequired,
	onSetActiveBreakpoints: PropTypes.func.isRequired,
	onSetMenu: PropTypes.func.isRequired,
	onSetOverlayVisibility: PropTypes.func.isRequired,
	onSetSubMenu: PropTypes.func.isRequired,
	onSetTertiaryMenu: PropTypes.func.isRequired,
	onUpdateLicense: PropTypes.func.isRequired,
	onUpdateMobile: PropTypes.func.isRequired,
	overlayIsVisible: PropTypes.bool,
	pageHeader: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	productLine: PropTypes.object,
	route: PropTypes.object,
	routes: PropTypes.array,
	selectedTertiaryMenu: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.array
	]),
	secondaryMenu: PropTypes.string,
	secondaryMenuContents: PropTypes.object,
	selectAdditional: PropTypes.array,
	setBreadcrumb: PropTypes.func.isRequired,
	setIsAuthenticated: PropTypes.func.isRequired,
	siteHeader: PropTypes.object,
	subscribeNewsletter: PropTypes.func.isRequired,
	subMenuSnapshot: PropTypes.string,
	user: PropTypes.object
};

App.defaultProps = {
	additionalMenuItems: null,
	alert: null,
	authenticationModal: null,
	breadcrumb: null,
	cart: null,
	cartLoading: null,
	children: null,
	error: null,
	footer: null,
	isAuthenticated: null,
	isMenuOpen: false,
	licenses: null,
	location: null,
	menu: null,
	menuContents: null,
	onIsMenuOpen: null,
	overlayIsVisible: null,
	pageHeader: null,
	productLine: null,
	route: null,
	routes: null,
	secondaryMenu: null,
	secondaryMenuContents: null,
	selectAdditional: null,
	selectedTertiaryMenu: null,
	subMenuSnapshot: null,
	siteHeader: null,
	user: null
};

const mapStateToProps = createStructuredSelector({
	additionalMenuItems: selectAdditional(),
	alert: selectAlert(),
	authenticationModal: selectAuthenticationModal(),
	breadcrumb: selectBreadcrumbState(),
	breakpoints: selectBreakpoints(),
	cart: selectCart(),
	cartLoading: selectCartLoading(),
	collections: selectCollections(),
	error: selectError(),
	footer: selectFooter(),
	isMenuOpen: selectIsMenuOpen(),
	isAuthenticated: selectAuthState(),
	licenses: selectLicenseState(),
	menu: selectMenu(),
	menuContents: selectMenuContents(),
	overlayIsVisible: selectOverlayState(),
	pageHeader: selectPageHeader(),
	preloadedSubMenusLoaded: selectPreloadedSubMenusLoaded(),
	productLine: selectProductLine(),
	secondaryMenu: selectSubMenu(),
	secondaryMenuContents: selectSubMenuContents(),
	selectedTertiaryMenu: selectTertiaryMenu(),
	subMenuSnapshot: selectSubMenuSnapshot(),
	siteHeader: selectSiteHeaderState(),
	user: selectUserProfile(),
	currentBlogTitle: selectCurrentBlogTitle()
});

function mapDispatchToProps(dispatch) {
	return {
		changeRoute: (url) => dispatch(push(url)),
		closeAuthenticationModal: () => dispatch(closeAuthenticationModalAction()),
		onAddToCart: (data) => dispatch(addToCart(data)),
		onClearError: () => dispatch(clearError()),
		onGetCart: () => dispatch(getCart()),
		onIsMenuOpen: (data) => dispatch(menuState(data)),
		onPreloadSubMenus: () => dispatch(preloadSubMenus()),
		onSetActiveBreakpoints: (updatedBreakpoints) => dispatch(setActiveBreakpoints(updatedBreakpoints)),
		onSetMenu: (menu) => dispatch(setMenu(menu)),
		onSetOverlayVisibility: (isVisible) => dispatch(setOverlayVisibility(isVisible)),
		onSetSubMenu: (menu) => dispatch(setSubMenu(menu)),
		onSetTertiaryMenu: (menu) => dispatch(setTertiaryMenu(menu)),
		onUpdateLicense: (updatedLicense) => dispatch(updateLicense(updatedLicense)),
		onUpdateMobile: (isMobile) => dispatch(setMobileState(isMobile)),
		setBreadcrumb: (data, breadcrumbBarType) => dispatch(setBreadcrumbState(data, breadcrumbBarType)),
		setIsAuthenticated: (isAuthenticated) => dispatch(setIsAuthenticated(isAuthenticated)),
		subscribeNewsletter: (data, callback) => dispatch(subscribeNewsletterAction(data, callback)),
		dispatch
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
